/**
 * Authentication Utilities
 * Handles redirect logic for SSO integration with auth.rolerelay.com
 * Uses token-based authentication for cross-domain SSO
 */

/**
 * Get the auth service URL (always auth.rolerelay.com)
 */
export const getAuthServiceUrl = (): string => {
  return 'https://auth.rolerelay.com';
};

/**
 * Get environment from config
 */
export const getCurrentEnvironment = (): 'staging' | 'production' | 'local' => {
  const env = process.env.REACT_APP_ENV;
  if (env === 'production') return 'production';
  if (env === 'local') return 'local';
  return 'staging';
};

/**
 * Build URL to redirect to auth.rolerelay.com with return URL
 * Requests token-based response for SSO compatibility
 */
export const buildAuthRedirectUrl = (
  currentUrl: string,
  env: 'staging' | 'production' | 'local'
): string => {
  const authUrl = new URL(getAuthServiceUrl());
  authUrl.searchParams.set('redirect', currentUrl);

  // Map local to staging for auth (auth service only supports staging/production)
  const authEnv = env === 'local' ? 'staging' : env;
  authUrl.searchParams.set('env', authEnv);

  // CRITICAL: Request token-based response for SSO compatibility
  // This tells rr-auth to return tokens in URL hash instead of creating a session cookie
  authUrl.searchParams.set('response_type', 'token');

  return authUrl.toString();
};

/**
 * Redirect user to auth service for login
 */
export const redirectToAuth = (): void => {
  const env = getCurrentEnvironment();
  const currentUrl = window.location.href;
  const authUrl = buildAuthRedirectUrl(currentUrl, env);

  window.location.href = authUrl;
};

/**
 * Redirect user to auth service for logout
 */
export const redirectToAuthLogout = (): void => {
  const env = getCurrentEnvironment();
  // Map local to staging for auth service
  const authEnv = env === 'local' ? 'staging' : env;
  const redirectUrl = getAppUrl(env);

  // Include response_type=token and redirect so if user logs in again, they get token-based auth
  window.location.href = `${getAuthServiceUrl()}?env=${authEnv}&response_type=token&redirect=${encodeURIComponent(redirectUrl)}`;
};

/**
 * Get the current app URL based on environment
 */
export const getAppUrl = (env: 'staging' | 'production' | 'local'): string => {
  if (env === 'local') {
    return window.location.origin; // Use current origin for local dev
  }
  return env === 'production'
    ? 'https://platform.rolerelay.com'
    : 'https://staging.rolerelay.com';
};
